import DOMPurify from 'dompurify';

/**
 * XSS Sanitization Utilities — Synnex Cloud Marketplace
 *
 * sanitizeHTML(dirty)
 *   Wraps DOMPurify for use with dangerouslySetInnerHTML.
 *   Preserves: all inline style="..." attributes, class, href (https), src,
 *              all structural HTML tags (div, p, table, ul, img, a, h1-h6, etc.)
 *   Strips:    <script>, <iframe>, <object>, <embed>, <form>, <base>, <link>
 *              All on* event handler attributes (onerror, onload, onclick, etc.)
 *              javascript: and data: URIs
 *
 * sanitizeTextInput(value)
 *   Light sanitizer for free-text form inputs (names, addresses, messages).
 *   Strips characters that could form HTML tags or JS execution vectors.
 *   Preserves: all normal text, numbers, spaces, punctuation, email addresses,
 *              URLs (https), Australian phone numbers, addresses.
 */

// DOMPurify config — strips execution vectors, keeps all visual HTML intact
const DOMPURIFY_CONFIG = {
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'base', 'link', 'meta', 'noscript'],
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
    'onchange', 'onsubmit', 'onkeydown', 'onkeyup', 'onkeypress',
    'onmousedown', 'onmouseup', 'onmouseenter', 'onmouseleave',
    'ondblclick', 'oncontextmenu', 'ondragstart', 'ondrop',
    'onscroll', 'onresize', 'onabort', 'oncanplay', 'onended',
    'oninput', 'oninvalid', 'onreset', 'onsearch', 'onselect',
    'ontoggle', 'onwheel', 'oncopy', 'oncut', 'onpaste',
    'onanimationstart', 'onanimationend', 'ontransitionend'
  ],
};

/**
 * Sanitize HTML for use in dangerouslySetInnerHTML.
 * Returns the { __html: sanitizedString } object directly.
 *
 * Usage:
 *   <div dangerouslySetInnerHTML={sanitizeHTML(content)} />
 *
 * @param {string} dirty - Raw HTML string from API/CMS
 * @returns {{ __html: string }} Safe object for dangerouslySetInnerHTML
 */
export const sanitizeHTML = (dirty) => {
  if (!dirty) return { __html: '' };
  const clean = DOMPurify.sanitize(String(dirty), DOMPURIFY_CONFIG);
  return { __html: clean };
};

/**
 * Sanitize free-text input field values before API submission.
 * Strips complete HTML tags and JS execution URI schemes.
 *
 * Called by deepSanitize() in the Axios request interceptor — runs at
 * submit-time, NOT in onChange. Users can type freely including characters
 * like < and > (e.g. comparisons, template syntax) without interference.
 *
 * What gets stripped:
 *   <anyTag ...>     — complete HTML/XML tags (e.g. <script>, <img onerror=...>)
 *   javascript:      — URI-based JS execution scheme
 *
 * What is preserved:
 *   Lone < or > characters not forming a complete tag (e.g. "3 < 5", "a > b")
 *   Letters, numbers, spaces, commas, periods, hyphens, apostrophes,
 *   @, +, /, colons (for URLs), parentheses, all standard punctuation,
 *   Syn-Sub2, O'Brien & Co., email addresses, URLs, phone numbers, addresses.
 *
 * @param {string} value - Raw string value from form state or API payload
 * @returns {string} Sanitized string safe for API submission
 */
export const sanitizeTextInput = (value) => {
  if (!value) return value;
  return String(value)
    .replace(/<[^>]*>/gi, '')     // strip complete HTML tags: <script>, <img onerror=...>, etc.
    .replace(/javascript:/gi, ''); // strip javascript: URI scheme
};

/**
 * Deep sanitize an object/array for use in Axios request interceptor.
 * Recursively walks the payload and sanitizes all string values.
 * Non-string values (numbers, booleans, null, arrays, objects) pass through unchanged.
 *
 * @param {*} value - Any value from a JSON request body
 * @returns {*} Sanitized version of the value
 */
export const deepSanitize = (value) => {
  if (typeof value === 'string') {
    return sanitizeTextInput(value);
  }
  if (Array.isArray(value)) {
    return value.map(deepSanitize);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, deepSanitize(v)])
    );
  }
  return value;
};